import React, { useContext, useCallback } from 'react'
import styled from 'styled-components'
import { fadeIn } from 'components/utils/animations'
import { getCustomerDetails, getFirstName, selectLoggedIn } from 'selectors/accountSelectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import { getActionIcon } from 'supportAssistant/functions/getActionIcon'
import { connect } from 'react-redux'
import { SocketContext } from 'supportAssistant/contexts/SocketContext'
import { generateChangeDatesItem, generateSelectDateCheckoutItem } from 'lib/checkout/accommodation/cart'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { getCheckoutPath, getBedbankChangeDatesPath } from 'lib/url/pathsUtils'
import { STEP_CAPACITY, STEP_CHANGE_DATES } from 'constants/booking'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { canCancelOrderItem, canChangeHotelRooms, canExtendHotelDates, getBedbankItem, getFreeCancellationPeriodEndDate, getHotelItem, isChangeDatesAllowed } from 'supportAssistant/utils/orderUtils'
import useNavigateTo from 'hooks/useNavigateTo'
import ModalContext from 'contexts/ModalContext'
import { BOOKING_CONFIRMATION_PAGE_ROUTE } from 'supportAssistant/constants/supportAssistant'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import { addChatMessage, createNewChat } from 'actions/supportAssistant/supportAssistantActions'
import { DMY_DATE_FORMAT } from 'constants/dateFormats'
import RequestAgentCallbackModal, { MappedRequestAgentCallbackResult } from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import qs from 'qs'
import { getFlashExtendDatesEnabled } from 'checkout/selectors/hotelSelectors'

// "if you see this dont use it.  Use moment.  Even though moment is evil.  it works perfectly" - Robert Harris
dayjs.extend(duration)
dayjs.extend(utc)

const StyledToggleButton = styled(TextButton)`
  animation: ${fadeIn} 1s;
`

interface MappedStateProps {
  customerId?: string,
  isLoggedIn?: boolean,
  customerDetails: any
  currentChatId: string | undefined
  isFlashExtendDatesEnabled: boolean
}

interface Props {
  action: any,
  payload?: App.ChatMessageMetaPayload,
  order?: App.Order,
  offer?: App.Offer
}

function Action(props: Props & MappedStateProps) {
  const {
    action,
    currentChatId,
    customerDetails,
    customerId,
    isFlashExtendDatesEnabled,
    isLoggedIn,
    offer,
    order,
    payload,
  } = props

  const callbackType: App.GenesysAgentCallbackType = 'LESA'

  // const [queuedLoginMessage, setQueuedLoginMessage] = useState<{message: string, chatMessageMeta?: App.ChatMessageMeta} | undefined>(undefined)

  const dispatch = useAppDispatch()
  const navigateTo = useNavigateTo()

  const { onLogin } = useContext(SocketContext)
  const showModal = useContext(ModalContext)

  const requestAgentCallback = useCallback(async(text: string) => {
    try {
      const results = await showModal<MappedRequestAgentCallbackResult | undefined>(
        <RequestAgentCallbackModal type={callbackType} chatId={currentChatId} />,
      )

      if (results) {
        let updatedPayload

        if (results.customerHasRecentlyScheduledCallback) {
          updatedPayload = {
            ...payload,
            customerHasRecentlyScheduledCallback: results.customerHasRecentlyScheduledCallback,
          }
        }
        else if (results.isImmediateCallback) {
          updatedPayload = {
            ...payload,
            isImmediateCallback: results.isImmediateCallback,
          }
        } else {
          updatedPayload = {
            ...payload,
            isImmediateCallback: results.isImmediateCallback,
            callbackStartTime: results.customer.callbackTime,
            callbackEndTime: addHalfHour(results.customer.callbackTime),
            callbackDate: dayjs(results.customer.callbackDate).format(DMY_DATE_FORMAT),
          }
        }

        dispatch(addChatMessage(text, { action: 'requestAgentCallback', payload: updatedPayload }))
      } else {
        dispatch(addChatMessage(text, { action: 'requestAgentCallbackCancel' }))
      }
    } catch {
      dispatch(addChatMessage(text, { action: 'requestAgentCallbackError' }))
    }
  }, [showModal, currentChatId, dispatch, payload])

  function addHalfHour(time) {
    const [hours, minutes] = time.split(':').map(Number)
    const date = dayjs().set('hour', hours).set('minute', minutes)

    const newDate = date.add(30, 'minute')

    return newDate.format('HH:mm')
  }

  const showLoginModal = useCallback(async(text: string, action: App.ChatActions) => {
    // Create the chat before we fire the login modal.
    // This ensures a smooth transition to logged in.
    if (currentChatId === 'default') {
      dispatch(createNewChat())
    }

    onLogin().then(({ loggedIn, customerId, firstName, lastName, phone, email }) => {
      if (loggedIn) {
        // The login modal's customer information is updated before the customer record in state is.
        // Because of this, we are grabbing the customer details from the socketContext onLogin function.
        // So that the details can be included in the messageMetaData payload.
        // This ensures we get the correct default message on login through the ChatActions
        // Once the user state has been updated, it is safe to inject the customer details into the payload
        // in the existing addChatMessage action

        if (action === 'requestAgentCallback') {
          requestAgentCallback(text)
          return
        }

        const updatedPayload = {
          ...payload,
          customerId,
          firstName,
          lastName,
          phone,
          email,
        }

        // setQueuedLoginMessage({ message: text, chatMessageMeta: { action, payload: updatedPayload } })
        dispatch(addChatMessage(text, { action, payload: updatedPayload }))
      }

      if (action === 'requestAgentCallback') {
        dispatch(addChatMessage(text, { action: 'loginCancelledWhenRequestingCallback' }))
      }
    })
  }, [currentChatId, dispatch, onLogin, payload, requestAgentCallback])

  const handleChangeDatesActionClick = useCallback(() => {
    if (!order) {
      return
    }

    if (order.items.length > 0) {
      if (offer) {
        const item = getHotelItem(order, payload?.itemId)
        const cartItem = generateChangeDatesItem(offer, order, item)

        dispatch(initialiseCheckout([cartItem], {
          regionCode: order?.regionCode,
          currencyCode: order?.currencyCode,
          postPurchase: 'change-dates',
          order,
        }))

        dispatch(pushWithRegion(getCheckoutPath(STEP_CHANGE_DATES)))
      }
    } else if (order.bedbankItems.length > 0) {
      const item = getBedbankItem(order)
      const changeDatesPath = getBedbankChangeDatesPath(item.id, order.id)
      navigateTo(changeDatesPath)
    }
  }, [dispatch, navigateTo, offer, order, payload])

  const handleAddDatesActionClick = useCallback(() => {
    if (!order) {
      return
    }

    if (order.items.length > 0) {
      if (offer) {
        const item = getHotelItem(order, payload?.itemId)
        const pkg = offer.packages.find(pkg => pkg.uniqueKey === item.package.uniqueKey)

        if (pkg) {
          const cartItem = generateSelectDateCheckoutItem(offer, pkg, order.id, item.id)

          dispatch(initialiseCheckout([cartItem], {
            postPurchase: 'select-date',
            regionCode: order.regionCode,
            currencyCode: order.currencyCode,
            order,
          }))

          dispatch(pushWithRegion(getCheckoutPath(STEP_CAPACITY)))
        }
      }
    }
  }, [dispatch, offer, order, payload])

  const handleCancelOrderActionClick = useCallback(() => {
    if (!order || !offer) {
      return
    }

    const item = getHotelItem(order, payload?.itemId)

    if (!canCancelOrderItem(order, item) || order.hasFlight) {
      return
    }

    const queryString = qs.stringify({ itemId: item.id, cancellation: true })
    dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, queryString))
  }, [dispatch, order, offer, payload])

  const handleCancelOutsidePolicyOrderActionClick = useCallback(() => {
    if (!order || !offer) {
      return
    }

    const item = getHotelItem(order, payload?.itemId)

    if (!getFreeCancellationPeriodEndDate(order, item) || order.hasFlight) {
      return
    }

    const queryString = qs.stringify({ itemId: item.id, refundRequest: true })
    dispatch(pushWithRegion(`${BOOKING_CONFIRMATION_PAGE_ROUTE}/${order.id}`, queryString))
  }, [dispatch, order, offer, payload])

  const handleClick = (actionItem: any) => {
    const chatActionKey: App.ChatActions = actionItem?.action?.key
    let item
    if (order) {
      item = getHotelItem(order, payload?.itemId)
    }

    if (chatActionKey === 'resetPasswordSent' && !isLoggedIn && !payload?.inputPayload) {
      dispatch(addChatMessage(actionItem?.text, { action: 'resetPasswordRequestEmail' }))
      return
    }

    if (chatActionKey === 'requestAgentCallback') {
      requestAgentCallback(actionItem?.text)
      return
    }

    const canChangeDates = isChangeDatesAllowed(payload, order, offer)
    const daysBeforeCheckInChangesDisallowed = offer?.daysBeforeCheckInChangesDisallowed
    if (order && chatActionKey === 'changeDates') {
      if (canChangeDates) {
        handleChangeDatesActionClick()
      }
    }

    if (order && offer && chatActionKey === 'orderAddDates') {
      if (item.status === 'awaiting_dates') {
        handleAddDatesActionClick()
      }
    }

    let canChangeRooms = false
    if (order && offer) {
      const items = order.items // note bedbank does not support room upgrades
      if (items?.length > 0) {
        canChangeRooms = canChangeHotelRooms(order, offer, items)
      }
    }

    if (order && !order.hasFlight && offer && chatActionKey === 'cancelOrder') {
      handleCancelOrderActionClick()
    }

    let freeCancellationPeriodEndDate: string | undefined
    if (order && item) {
      freeCancellationPeriodEndDate = getFreeCancellationPeriodEndDate(order, item)
    }

    if (order && !order.hasFlight && offer && chatActionKey === 'cancelOrder' && freeCancellationPeriodEndDate) {
      handleCancelOutsidePolicyOrderActionClick()
    }

    if (!isLoggedIn) {
      switch (chatActionKey) {
        case 'listOrders':
          showLoginModal(actionItem?.text, 'listOrders')
          return
        case 'login':
          showLoginModal(actionItem?.text, 'login')
          return
        case 'loginWithDefaultActions':
          showLoginModal(actionItem?.text, 'loginWithDefaultActions')
          return
        case 'loginWithBookingID':
          showLoginModal(actionItem?.text, 'loginWithBookingID')
          return
        case 'requestAgent':
          showLoginModal(actionItem?.text, 'requestAgent')
          return
      }
    }

    const canExtendDates = isFlashExtendDatesEnabled && canChangeDates && canExtendHotelDates(order, offer, payload?.itemId)

    // If we have come from the logged out state the users details are not included in the payload.
    // It is safe to use the customer record in state here as as been updated by the time we reach here.
    const updatedPayload = {
      ...payload,

      // If customer only has 1 order we need to inject these values
      canChangeDates,
      canChangeRooms,
      canExtendDates,
      daysBeforeCheckInChangesDisallowed,
      freeCancellationPeriodEndDate,

      customerId: payload?.customerId ? payload.customerId : customerId,
      firstName: payload?.firstName ? payload.firstName : customerDetails.firstName,
      checkInDate: item?.reservation?.startDate ? item.reservation.startDate : payload?.checkInDate,
    }

    dispatch(addChatMessage(actionItem?.text, { action: chatActionKey, payload: updatedPayload }))
  }

  const ActionIcon = getActionIcon(action?.category)

  return (
    <StyledToggleButton
      kind="secondary"
      variant="dark"
      onClick={() => handleClick(action)}
      startIcon={ActionIcon ? <ActionIcon /> : undefined}
    >
      {action?.text}
    </StyledToggleButton>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    isLoggedIn: selectLoggedIn(state),
    customerId: state.auth.account.memberId,
    firstName: getFirstName(state),
    customerDetails: getCustomerDetails(state),
    currentChatId: state.supportAssistant.currentChatId,
    isFlashExtendDatesEnabled: getFlashExtendDatesEnabled(state),
  }
})(Action)
